import apiV2 from "api-v2";
import type {
	DigestSettings,
	NotificationDeliveryFilters,
	NotificationDeliveryResponse,
	NotificationScopeSelection,
	NotificationTypeResource,
	UpdateNotificationPreferencesPayload,
} from "../types/Notifications.type";

// apiV2 has no unwrap interceptor (see RolesPermissions/api/rbac-api.ts) — Laravel API
// Resources arrive as the raw `{ data: ... }` envelope, so unwrap here rather than at
// the call site.
const unwrap = <T>(responseData: unknown): T => {
	if (responseData && typeof responseData === "object" && "data" in responseData) {
		return (responseData as Record<string, unknown>).data as T;
	}
	return responseData as T;
};

export const getNotificationTypes = async (
	scope: NotificationScopeSelection
): Promise<NotificationTypeResource[]> => {
	const response = await apiV2.get<{ data: NotificationTypeResource[] } | NotificationTypeResource[]>(
		"/notifications/types",
		{
			params: {
				scope_type: scope.scopeType,
				scope_id: scope.scopeId,
			},
		}
	);

	return unwrap<NotificationTypeResource[]>(response.data);
};

export const updateNotificationPreferences = async (
	payload: UpdateNotificationPreferencesPayload
): Promise<NotificationTypeResource[]> => {
	const response = await apiV2.put<{ data: NotificationTypeResource[] } | NotificationTypeResource[]>(
		"/notifications/preferences",
		payload
	);

	return unwrap<NotificationTypeResource[]>(response.data);
};

export const updateDigestSettings = async (payload: DigestSettings): Promise<DigestSettings> => {
	const response = await apiV2.put<{ data: DigestSettings } | DigestSettings>(
		"/notifications/digest-settings",
		payload
	);

	return unwrap<DigestSettings>(response.data);
};

export const getDeliveryAudit = async (
	filters: NotificationDeliveryFilters
): Promise<NotificationDeliveryResponse> => {
	const response = await apiV2.get<NotificationDeliveryResponse>("/notifications/deliveries", {
		params: filters,
	});

	return response.data;
};

// Mirrors buildAuditExportUrl (RolesPermissions/api/rbac-api.ts) — direct anchor
// navigation to the backend export endpoint, NOT an axios/fetch call. The backend
// streams text/csv with Content-Disposition: attachment, so the browser downloads
// the file itself; fetching and re-saving here would mean reformatting the CSV
// client-side, which the brief explicitly disallows (item-01 FE-5 pattern).
export const buildDeliveryExportUrl = (filters: Omit<NotificationDeliveryFilters, "page">): string => {
	const baseUrl = apiV2.defaults.baseURL ?? "";
	const searchParams = new URLSearchParams();

	Object.entries(filters).forEach(([key, value]) => {
		if (value !== undefined && value !== null && value !== "") {
			searchParams.set(key, String(value));
		}
	});

	const queryString = searchParams.toString();
	const path = `${baseUrl}/notifications/deliveries/export`;

	return queryString ? `${path}?${queryString}` : path;
};
